import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Eye, EyeOff, ChevronUp, ChevronDown, Trash2, Pencil } from "lucide-react";

export default function PagesList() {
  const [pages, setPages] = useState([]);
  const nav = useNavigate();
  const load = () => api.get("/admin/pages").then((r) => setPages(r.data));
  useEffect(() => { load(); }, []);

  const patch = async (p, patchObj) => {
    try {
      await api.put(`/admin/pages/${p.id}`, { ...p, ...patchObj });
      load();
    } catch (e) { toast.error("Could not save"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/pages/${p.id}`); load(); toast.success("Page deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const move = async (idx, dir) => {
    const arr = [...pages];
    const j = idx + dir; if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setPages(arr);
    try { await api.post("/admin/pages/reorder", arr.map((p) => p.id)); }
    catch { toast.error("Reorder failed"); load(); }
  };

  const addBlank = async () => {
    try {
      const r = await api.post("/admin/pages", { title: "Untitled Page", active: false, show_in_footer: false, indexable: true });
      nav(`/admin/pages/${r.data.id}`);
    } catch { toast.error("Create failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-3">
        <h1 className="font-display text-2xl sm:text-3xl text-gold">Pages</h1>
        <button onClick={addBlank} className="btn-gold text-xs sm:text-sm inline-flex items-center gap-2" data-testid="pages-add">
          <Plus size={16} /> Add Page
        </button>
      </div>

      {pages.length === 0 && (
        <div className="curio-card p-8 text-center text-parchment/60 italic">No pages yet — add one to get started.</div>
      )}

      <div className="grid grid-cols-1 gap-3">
        {pages.map((p, idx) => (
          <div key={p.id} className="curio-card p-4 flex flex-col sm:flex-row gap-3 sm:items-center" data-testid={`page-row-${p.slug}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-display text-lg text-cream truncate">{p.title}</div>
                {!p.active && <span className="font-caption text-[10px] px-2 py-0.5 border border-brass/40 text-brass">DRAFT</span>}
                {p.show_in_footer && p.active && <span className="font-caption text-[10px] px-2 py-0.5 border border-gold/50 text-gold">IN FOOTER</span>}
                {!p.indexable && <span className="font-caption text-[10px] px-2 py-0.5 border border-oxblood/60 text-oxblood">NOINDEX</span>}
              </div>
              <div className="font-caption text-[11px] text-parchment/50 mt-1">/page/{p.slug}</div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button title={p.active ? "Set inactive" : "Set active"} onClick={() => patch(p, { active: !p.active })}
                data-testid={`page-toggle-active-${p.slug}`}
                className={`p-2 border ${p.active ? "border-gold text-gold" : "border-brass/40 text-brass"}`}>
                {p.active ? <Eye size={14} /> : <EyeOff size={14} />}
              </button>
              <button title="Move up" onClick={() => move(idx, -1)} className="p-2 border border-brass/40 text-brass" data-testid={`page-up-${p.slug}`}><ChevronUp size={14} /></button>
              <button title="Move down" onClick={() => move(idx, 1)} className="p-2 border border-brass/40 text-brass" data-testid={`page-down-${p.slug}`}><ChevronDown size={14} /></button>
              <Link to={`/admin/pages/${p.id}`} className="p-2 border border-brass/40 text-brass hover:text-gold" data-testid={`page-edit-${p.slug}`}><Pencil size={14} /></Link>
              <button title="Delete" onClick={() => remove(p)} className="p-2 border border-oxblood/60 text-oxblood hover:bg-oxblood/10" data-testid={`page-delete-${p.slug}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
